import { BodyMeasurements } from "../models/BodyMeasurements";
import { User } from "../models/User";

export const MEALTIME_SPLIT: Record<string, number> = {
  BREAKFAST: 0.25,
  SNACK_AM: 0.1,
  LUNCH: 0.3,
  SNACK_PM: 0.1,
  DINNER: 0.25,
};

export type MacroKey = "ENERGY" | "PROTEIN" | "FAT" | "CARBOHYDRATE";
export type DailyNutrients = Record<MacroKey, number>;
export type MealtimeNutrients = Record<string, Record<MacroKey | "FIBRE", number>>;

const MACRO_KEYS: MacroKey[] = ["ENERGY", "PROTEIN", "FAT", "CARBOHYDRATE"];

export const emptyDailyNutrients = (): DailyNutrients => ({
  ENERGY: 0,
  PROTEIN: 0,
  FAT: 0,
  CARBOHYDRATE: 0,
});

export const buildMealtimeNutrients = (dailyNutrients: DailyNutrients): MealtimeNutrients => {
  const result: MealtimeNutrients = {};

  Object.entries(MEALTIME_SPLIT).forEach(([mealtime, ratio]) => {
    result[mealtime] = {
      ENERGY: dailyNutrients.ENERGY * ratio,
      PROTEIN: dailyNutrients.PROTEIN * ratio,
      FAT: dailyNutrients.FAT * ratio,
      CARBOHYDRATE: dailyNutrients.CARBOHYDRATE * ratio,
      FIBRE: 0,
    };
  });

  return result;
};

const sanitizeDailyNutrients = (nutrients?: Record<string, number> | null): DailyNutrients | null => {
  if (!nutrients) return null;

  const sanitized = emptyDailyNutrients();
  MACRO_KEYS.forEach((key) => {
    const value = Number(nutrients[key]);
    sanitized[key] = Number.isFinite(value) ? value : 0;
  });

  return MACRO_KEYS.some((key) => sanitized[key] > 0) ? sanitized : null;
};

const sanitizeMealtimeNutrients = (
  mealtimeNutrients?: Record<string, Record<string, number>> | null
): MealtimeNutrients | null => {
  if (!mealtimeNutrients) return null;

  const sanitized: MealtimeNutrients = {};

  Object.keys(MEALTIME_SPLIT).forEach((mealtime) => {
    const source = mealtimeNutrients[mealtime];
    if (!source) return;

    sanitized[mealtime] = {
      ENERGY: Number.isFinite(Number(source.ENERGY)) ? Number(source.ENERGY) : 0,
      PROTEIN: Number.isFinite(Number(source.PROTEIN)) ? Number(source.PROTEIN) : 0,
      FAT: Number.isFinite(Number(source.FAT)) ? Number(source.FAT) : 0,
      CARBOHYDRATE: Number.isFinite(Number(source.CARBOHYDRATE)) ? Number(source.CARBOHYDRATE) : 0,
      FIBRE: Number.isFinite(Number(source.FIBRE)) ? Number(source.FIBRE) : 0,
    };
  });

  const hasAnyValue = Object.values(sanitized).some((nutrients) =>
    MACRO_KEYS.some((key) => nutrients[key] > 0)
  );

  return hasAnyValue ? sanitized : null;
};

const deriveDailyFromMealtimeNutrients = (
  mealtimeNutrients?: Record<string, Record<string, number>> | null
): DailyNutrients | null => {
  const sanitized = sanitizeMealtimeNutrients(mealtimeNutrients);
  if (!sanitized) return null;

  const totals = emptyDailyNutrients();
  Object.values(sanitized).forEach((nutrients) => {
    MACRO_KEYS.forEach((key) => {
      totals[key] += nutrients[key] ?? 0;
    });
  });

  return MACRO_KEYS.some((key) => totals[key] > 0) ? totals : null;
};

const calculateDailyFromProfile = (user?: User | null): DailyNutrients | null => {
  if (!user) return null;

  const actual = user.bodyMeasurements?.find((measurement) => measurement.type === BodyMeasurements.type.ACTUAL);
  const plan = user.bodyMeasurements?.find((measurement) => measurement.type === BodyMeasurements.type.PLAN);

  const weightKg = actual?.weight;
  const heightCm = actual?.height;
  const age = user.age;
  const sex = user.sex;
  const activityLevel = user.physicalActivityLevel ?? 1.2;
  const targetWeightKg = plan?.weight;
  const targetDate = plan?.targetDate;

  if (!weightKg || !heightCm || !age || !sex) {
    return null;
  }

  const baseBmr = 10 * weightKg + 6.25 * heightCm - 5 * age;
  const bmr = sex === User.sex.FEMALE ? baseBmr - 161 : baseBmr + 5;
  const tdee = bmr * activityLevel;

  let dailyDelta = 0;
  if (targetWeightKg && weightKg) {
    const weightDiff = targetWeightKg - weightKg;
    if (targetDate) {
      const days = Math.max(
        1,
        Math.round((new Date(targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      );
      dailyDelta = (weightDiff * 7700) / days;
    } else if (weightDiff !== 0) {
      dailyDelta = Math.sign(weightDiff) * 300;
    }
  }

  dailyDelta = Math.max(-1000, Math.min(1000, dailyDelta));

  const dailyCalories = Math.max(0, tdee + dailyDelta);
  const baseWeight = targetWeightKg || weightKg;
  const protein = Math.max(0, baseWeight * 1.6);
  const fat = Math.max(0, baseWeight * 0.8);
  const remainingCalories = dailyCalories - protein * 4 - fat * 9;
  const carbohydrate = Math.max(0, remainingCalories / 4);

  return {
    ENERGY: dailyCalories,
    PROTEIN: protein,
    FAT: fat,
    CARBOHYDRATE: carbohydrate,
  };
};

export const calculateNutritionTargets = (user?: User | null) => {
  const dailyNutrients = calculateDailyFromProfile(user) ?? emptyDailyNutrients();
  return {
    dailyNutrients,
    mealtimeNutrients: buildMealtimeNutrients(dailyNutrients),
  };
};

export const normalizeUserNutrition = (user: User, fallbackUser?: User | null): User => {
  const normalizedDaily =
    sanitizeDailyNutrients(user.dailyNutrients) ??
    calculateDailyFromProfile(user) ??
    sanitizeDailyNutrients(fallbackUser?.dailyNutrients) ??
    deriveDailyFromMealtimeNutrients(user.mealtimeNutrients) ??
    deriveDailyFromMealtimeNutrients(fallbackUser?.mealtimeNutrients) ??
    emptyDailyNutrients();

  return {
    ...user,
    dailyNutrients: normalizedDaily,
    mealtimeNutrients: buildMealtimeNutrients(normalizedDaily),
  };
};
